import { $getSelection, $isRangeSelection, $createTextNode } from "lexical";
import { $createLinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import LinkIcon from "../../../icons/richicons/linkicon";
import { useTranslation } from "../../../core/translation.core";
import { RefObject, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { inputCss } from "../../reuseables/native.comp";
import { toast } from "react-toastify";


type LinkFrom = {
  url: string;
  title?: string;
}

export function InsertLinkButton() {
  const [editor] = useLexicalComposerContext();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const insertLink = (url: string, title?: string) => {
    if (!url || url.length < 1) {
      toast.error("Invalid URL");
      return;
    }
  
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const linkNode = $createLinkNode(url);
        linkNode.append($createTextNode(title || url));
        selection.insertNodes([linkNode]);
      }
    });
    dialogRef.current?.close();
  };

  const clickCancelled = () => {
    dialogRef.current?.close();
  };

  const clickAddLink = () => {
    dialogRef.current?.showModal();
  };

  return (
    <>
      <button
        onClick={clickAddLink}
        className="text-sm pb-1"
        type="button"
      >
      <LinkIcon className="w-5 h-5" />
      </button>
      <LinkDialog error={error} insertLink={insertLink} dialogRef={dialogRef} clickCancelled={clickCancelled} />
    </>
  );
}


function LinkDialog({ dialogRef, clickCancelled, insertLink, error } : {dialogRef: RefObject<HTMLDialogElement>; clickCancelled: () => void; insertLink: (url: string, title?: string) => void; error: string | null}) {
  const { t, msg } = useTranslation();
  const methods = useForm<LinkFrom>({
    defaultValues: {
      url: "",
      title: "",
    },
  });
 
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      if (!inside) {
        dialog.close();
        clickCancelled();
      }
    };

    dialog.addEventListener("click", handleOutsideClick);
    return () => dialog.removeEventListener("click", handleOutsideClick);
  }, [dialogRef, clickCancelled]);

  return (
    <dialog className="p-8 gap-4 dark:bg-her-backblue dark:text-white rounded-lg min-w-[450px]" ref={dialogRef}>
      <span className="text-sm">{t(msg.product.addLink)}</span>
      <div className="flex flex-col gap-8 mt-3 mb-2">
        <input
          type="text"
          placeholder="Enter URL"
          {...methods.register("url")}
          className={`${inputCss}`}
        />
        <input
          type="text"
          placeholder="Enter Title"
          {...methods.register("title")}
          className={`${inputCss}`}
        />
      </div>
      {/* {error && <span className="text-red-500 text-sm my-2">{error}</span>} */}
      <div className="mt-3 w-full items-center justify-end flex gap-3">
        <button className="bg-red-500 text-white px-2 py-1 rounded-lg" onClick={clickCancelled} type="button">{t(msg.form.cancel)}</button>
        <button 
          className={`disabled:bg-slate-300 disabled:cursor-not-allowed bg-her-dodgerblue text-white px-4 py-1 rounded-lg`} 
          onClick={() => insertLink(methods.getValues("url"), methods.getValues("title"))} 
          type="button">{t(msg.form.save)}
        </button>
      </div>
    </dialog>
  )
}